// Refer to ARCHITECTURE.md for the candidate data flow; this
// component calls GET /api/activity/applications/:id as shown in
// the architecture diagram.
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./MyApplications.css";

export default function MyApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  
  // Get current user ID from localStorage
  const candidateId = localStorage.getItem("candidateId");

  useEffect(() => {
    if (!candidateId) {
      navigate("/employee/home");
      return;
    }
    loadApplications();
  }, [candidateId, navigate]);

  async function loadApplications() {
    try {
      const response = await fetch(
        ` /api/activity/applications/${candidateId}`
      );
      const data = await response.json();
      
      if (data.success) {
        setApplications(data.data || []);
      } else {
        console.error("Failed to load applications:", data.error);
      }
    } catch (error) {
      console.error("Error loading applications:", error);
    } finally {
      setLoading(false);
    }
  }

  // Filter applications by status
  const filteredApplications = 
    filter === "all" 
      ? applications 
      : applications.filter(app => app.status === filter);

  if (loading) {
    return (
      <div className="my-applications loading-container">
        <div className="spinner"></div>
        <p>Loading your applications...</p>
      </div>
    );
  }

  if (!candidateId) {
    return (
      <div className="my-applications">
        <div className="empty-state">
          <h2>Please log in to view your applications</h2>
          <button onClick={() => navigate("/login")}>Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-applications">
      <div className="ma-header">
        <h1>My Applications</h1>
        <p className="ma-subtitle">
          You have {filteredApplications.length} application{filteredApplications.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="ma-filters">
        <button 
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All ({applications.length})
        </button>
        <button 
          className={`filter-btn ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          Pending ({applications.filter(a => a.status === "pending").length})
        </button>
        <button 
          className={`filter-btn ${filter === "approved" ? "active" : ""}`}
          onClick={() => setFilter("approved")}
        >
          Approved ({applications.filter(a => a.status === "approved").length})
        </button>
        <button 
          className={`filter-btn ${filter === "rejected" ? "active" : ""}`}
          onClick={() => setFilter("rejected")}
        >
          Rejected ({applications.filter(a => a.status === "rejected").length})
        </button>
      </div>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <div className="ma-empty">
          <div className="empty-icon">📄</div>
          <h2>No applications yet</h2>
          <p>
            {filter === "all" 
              ? "You haven't applied to any jobs yet. Start exploring!" 
              : `No ${filter} applications`}
          </p>
          <button 
            className="ma-btn-primary"
            onClick={() => navigate("/jobs")}
          >
            Browse Job Listings
          </button>
        </div>
      ) : (
        <div className="ma-list">
          {filteredApplications.map(app => (
            <div key={app.id} className={`ma-card status-${app.status || "pending"}`}>
              <div className="ma-card-header">
                <div className="ma-card-title-section">
                  <h3 className="ma-job-title">{app.title || "Job Title"}</h3>
                  <p className="ma-company">{app.company_name || "Company Name"}</p>
                </div>
                <span className={`ma-status-badge status-${app.status || "pending"}`}>
                  {(app.status || "pending").toUpperCase()}
                </span>
              </div>

              <div className="ma-card-body">
                <div className="ma-detail">
                  <span className="ma-label">📍 Location:</span>
                  <span className="ma-value">{app.location || "Not specified"}</span>
                </div>
                
                <div className="ma-detail">
                  <span className="ma-label">💰 Salary Range:</span>
                  <span className="ma-value">
                    {app.salary_min || app.salary_max 
                      ? `₹${(app.salary_min || 0).toLocaleString()} - ₹${(app.salary_max || 0).toLocaleString()}`
                      : "Not specified"}
                  </span>
                </div>

                <div className="ma-detail">
                  <span className="ma-label">📅 Applied On:</span>
                  <span className="ma-value">
                    {app.applied_at 
                      ? new Date(app.applied_at).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short",
                          day: "numeric"
                        })
                      : "Date not available"}
                  </span>
                </div>

                {app.description && (
                  <div className="ma-detail">
                    <span className="ma-label">📝 Description:</span>
                    <p className="ma-description">
                      {app.description.substring(0, 150)}
                      {app.description.length > 150 ? "..." : ""}
                    </p>
                  </div>
                )}
              </div>

              <div className="ma-card-footer">
                <div className="ma-status-info">
                  {app.status === "approved" && (
                    <div className="ma-status-message success">
                      ✅ Great! Your application has been approved.
                    </div>
                  )}
                  {app.status === "rejected" && (
                    <div className="ma-status-message rejected">
                      ❌ Unfortunately, your application was not selected.
                    </div>
                  )}
                  {app.status === "pending" && (
                    <div className="ma-status-message pending">
                      ⏳ Your application is under review. We'll update you soon.
                    </div>
                  )}
                </div>
                
                <button 
                  className="ma-btn-secondary"
                  onClick={() => navigate(`/jobs/${app.job_id}`)}
                >
                  View Job
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
